import type { TicketBrief, TicketProblem } from '../api/easeedesk'
import './TicketBands.css'

/**
 * Cele doua benzi din capul fisei: rudenia si problema.
 *
 * `easeedesk/ticket/_family.php` si `_problems.php`. Amandoua se deseneaza
 * NUMAI cand au ce spune: un tichet obisnuit n-are de ce sa capete un chenar
 * care sa-i spuna ca n-are parinte si nicio cauza cunoscuta.
 *
 * Foaia lor a plecat din markup intr-un fisier alaturi. In portal statea intr-un
 * `<style>` scris o singura data pe pagina, cu un `static $styleWritten`,
 * fiindca randul extins se poate deschide de pe vreo optsprezece ecrane, fiecare
 * cu lista lui de foi. Aici e un import, deci ajunge o data in pachet oricat de
 * multe ecrane l-ar folosi, si nu mai are nevoie de paznic.
 */

export function TicketFamily({
  parent,
  kids,
  causedBy = [],
  caused = [],
}: {
  parent: TicketBrief | null
  kids: TicketBrief[]
  /** Change-urile care au cauzat incidentul (legatura "cauzat de change"). */
  causedBy?: TicketBrief[]
  /** Incidentele cauzate de change-ul asta. */
  caused?: TicketBrief[]
}) {
  if (!parent && kids.length === 0 && causedBy.length === 0 && caused.length === 0) return null

  /*
   * Peste cinci copii, lista capata margini si se deruleaza. O cerere cu
   * douasprezece dependente impingea tot ce urmeaza cu un ecran mai jos, iar
   * banda e un indiciu, nu continutul fisei.
   */
  const scrolls = kids.length > 5

  return (
    <div className="tk-family">
      {parent && (
        <div className="tk-family-row">
          <span className="tk-family-label">Part of</span>
          <FamilyItem ticket={parent} />
        </div>
      )}

      {kids.length > 0 && (
        <div className="tk-family-row">
          <span className="tk-family-label">
            Depends on <span className="tk-family-count">{kids.length}</span>
          </span>
          <div className={`tk-family-list${scrolls ? ' tk-family-scroll' : ''}`}>
            {kids.map((child) => (
              <FamilyItem key={child.id} ticket={child} />
            ))}
          </div>
        </div>
      )}

      {/*
       * Legatura "cauzat de change", pe amandoua capetele: incidentul arata
       * change-ul care l-a stricat, change-ul arata ce a lasat in urma. E
       * intrebarea cu care se deschide orice analiza post-implementare.
       */}
      {causedBy.length > 0 && (
        <div className="tk-family-row tk-family-caused-by">
          <span className="tk-family-label">Caused by change</span>
          <div className="tk-family-list">
            {causedBy.map((change) => (
              <FamilyItem key={change.id} ticket={change} />
            ))}
          </div>
        </div>
      )}

      {caused.length > 0 && (
        <div className="tk-family-row tk-family-caused">
          <span className="tk-family-label">
            Caused incidents <span className="tk-family-count">{caused.length}</span>
          </span>
          <div className={`tk-family-list${caused.length > 5 ? ' tk-family-scroll' : ''}`}>
            {caused.map((incident) => (
              <FamilyItem key={incident.id} ticket={incident} />
            ))}
          </div>
        </div>
      )}
    </div>
  )
}

/**
 * Un tichet, ca legatura, cu starea lui.
 *
 * In fila noua: banda se citeste stand pe un tichet, iar cine se uita la parinte
 * vrea sa compare, nu sa plece. `rel` merge cu `target`, altfel pagina noua
 * capata acces la asta prin `window.opener`.
 */
function FamilyItem({ ticket }: { ticket: TicketBrief }) {
  return (
    <a className={`tk-family-item${ticket.closed ? ' tk-family-closed' : ''}`} href={`/app/easeedesk/ticket/${ticket.id}`} target="_blank" rel="noopener noreferrer">
      <strong>#{ticket.id}</strong>
      <span className="tk-family-subject">{ticket.subject}</span>
      {ticket.status !== '' && (
        <span className="tk-family-status" style={{ background: ticket.color }}>
          {ticket.status}
        </span>
      )}
    </a>
  )
}

export function TicketProblems({ problems }: { problems: TicketProblem[] }) {
  if (problems.length === 0) return null

  return (
    <>
      {problems.map((problem) => (
        <p className="tk-problem" key={problem.id}>
          <i className="fas fa-sitemap" style={{ color: '#8a4b1c' }}></i> Part of{' '}
          <a href={`/app/problems/view/${problem.id}`} target="_blank" rel="noopener noreferrer">
            problem #{problem.id} — {problem.title}
          </a>
          <span className="tk-problem-status" style={{ background: problem.color }}>
            {problem.statusLabel}
          </span>
          {problem.workaround !== '' && (
            /*
             * Ocolirea se scrie intreaga, aici, nu se ascunde dupa o legatura.
             * Cine citeste banda e in mijlocul unei interventii, si un click in
             * plus inseamna ca reface el diagnosticul de la zero - adica exact
             * ce exista problema ca sa nu se mai intample.
             */
            <span className="tk-problem-workaround">
              <i className="fas fa-life-ring"></i> {problem.workaround}
            </span>
          )}
        </p>
      ))}
    </>
  )
}
